// @ts-check
/* eslint no-param-reassign: ["error", { "props": false }] */

/**
* A field, displayable on the page
* @typedef {Object} Field
* @property {string} group - Indicates whether the Courage component is present.
*/

import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import * as Sentry from '@sentry/browser'
import amplitude from 'amplitude-js'
import setWith from 'lodash.setwith'
import router from '../router'

import auth from './modules/auth'
import featureFlags from './modules/featureFlags'

Vue.use(Vuex)

const store = new Vuex.Store({
  strict: false, // false because setCurrentFieldValue causes error when setting drawn signatures, even though it is a designated mutation
  modules: {
    auth,
    featureFlags
  },
  state: {
    logoURL: '',
    hasBranding: false,
    config: {},
    envelope: {
      documents: [],
      title: null,
      id: null,
      status: null
    },
    signatures: {
      drawn: null,
      typed: null,
      uploaded: null,
      signatureData: null,
      encodedJPG: null
    },
    parties: [],
    colours: {},
    currentDocumentIndex: 0,
    activeFieldId: null,
    signatureToUse: '',
    latestSignature: {},
    uploaders: {},
    requestedEnvelope: {
      envelope_fingerprint: null,
      signature_fingerprint: null,
      widgetCode: null
    },
    widget: {},
    /**
     * @type {number[]}
     */
    touchedFieldIds: []
  },
  mutations: {
    setWidget: (state, { widget, mergeFields }) => {
      state.widget = widget

      if (mergeFields) {
        state.widget.mergeFields = Object.keys(mergeFields).map((key) => { // eslint-disable-line arrow-body-style
          return {
            fieldName: key,
            value: mergeFields[key]
          }
        })
      }

      Object.values(state.widget.fields).forEach((page) => {
        page.forEach((field) => {
          field.content = ''
          if (field.merge_field) {
            const mf = state.widget.mergeFields.find(mergeField => mergeField.fieldName === field.merge_field)
            if (mf && mf.value) {
              field.content = mf.value
              field.prefilled = true
            }
            field.is_merge_field = true
          }
        })
      })

      state.config = widget.config // Set company config
      state.signatureToUse = translateSignatureTypes(widget.config.company_signature_default)

      state.envelope.title = widget.title // Set envelope details
      state.envelope.id = widget.id
      state.envelope.pages = widget.pages

      state.colours = widget.colours // Set colours

      state.logoURL = widget.logoURL // Set branding
      state.hasBranding = widget.hasBranding // Set branding
    },
    setEnvelopeRegular: (state, { data }) => {
      state.config = data.config // Set company config
      state.signatureToUse = translateSignatureTypes(data.config.company_signature_default)

      state.envelope.title = data.envelope_title // Set envelope details
      state.envelope.id = data.envelope_id
      state.envelope.status = data.status

      state.colours = data.colours // Set colours

      state.logoURL = data.logoURL // Set branding
      state.hasBranding = data.hasBranding // Set branding

      data.parties.forEach((party) => { // Set parties
        state.parties.push(party)
      })

      /**
       * Logging info for potential issues - https://gitlab.wearedomo.com/domo/signable/signing-page/-/issues/166
       *
       * Wrapping it in a function so we can set sentry scope *only for capturing the message*, so as not to
       * pollute sentry
       */
      /* eslint-disable camelcase */
      const { envelope_fingerprint, signature_fingerprint } = state.requestedEnvelope
      const captureSentryMessage = (message) => {
        Sentry.captureMessage(message)
        Sentry.configureScope((scope) => {
          scope.setExtra('URL', `https://sign.signable.app/#/${envelope_fingerprint}/${signature_fingerprint}`)
        })
      }
      if (!data.documents.length) {
        captureSentryMessage(`NaN Issue: Documents not loaded for envelope: ${envelope_fingerprint}; signature: ${signature_fingerprint}. Given type: ${typeof data.documents}`)
      }
      if (data.documents) { // don't error if the JSON payload is incomplete
        /**
         * the following 2 conditions might be thrown in SideBarInfo
         * component https://gitlab.wearedomo.com/domo/signable/signing-page/blob/25ef91a0e3ba462ace1478a0f8994fbf3bd3b437/src/components/SideBarInfo.vue#L21
         */
        if (!state.widget.fields && !data.documents[0].num_pages) {
          captureSentryMessage(`NaN Issue: Property "num_pages" not provided. Envelope: ${envelope_fingerprint}; signature: ${signature_fingerprint}. Given type: ${typeof data.documents[0].num_pages}`)
        }
        if (!!state.widget.fields && !data.documents.pages) {
          captureSentryMessage(`NaN Issue: Property "pages" not provided. Envelope: ${envelope_fingerprint}; signature: ${signature_fingerprint}. Given type: ${typeof data.documents.pages}`)
        }
      }
      /* eslint-enable camelcase */

      data.documents.forEach((doc) => { // Set documents
        state.envelope.documents.push(doc)
      })

      state.envelope.documents.forEach((document) => {
        Object.values(document.fields).forEach((page) => {
          page.forEach((field) => {
            if (field.merge_field && field.content) field.prefilled = true
          })
        })
      })
    },
    setActiveField: (state, activeFieldId) => {
      state.activeFieldId = activeFieldId
    },
    setFieldTouched: (state, id) => {
      if (!state.touchedFieldIds.includes(id)) {
        state.touchedFieldIds.push(id)
      }
    },
    setCurrentFieldValue: (state, { field, val, property, isWidget }) => {
      const updatedFields = isWidget ? state.widget.fields : state.envelope.documents[field.docIndex].fields

      updatedFields[field.page][field.index][property] = val
      setWith(updatedFields, `${field.page}.${field.index}.${property}`, val, function (nsValue, key, nsObject) {
        return Vue.set(nsObject, key, nsValue)
      })
    },
    setSignatureToUse: (state, tabProperty) => {
      state.signatureToUse = tabProperty
    },
    setLatestSignature: (state, value) => {
      state.latestSignature = { ...state.latestSignature, ...value }
    },
    setUploader: (state, { uploader, fieldId }) => {
      state.uploaders[fieldId] = uploader
    },
    setDocument: (state, documentIndex) => {
      state.currentDocumentIndex = documentIndex
    },
    setRequestedEnvelope: (state, requestedEnvelope) => {
      state.requestedEnvelope = requestedEnvelope
    }
  },
  actions: {
    getEnvelope: ({ getters, commit }, { query, password, widgetCode }) => {
      if (router.currentRoute.name === 'SignWidget' || router.currentRoute.name === 'WidgetComplete') {
        return axios.get(`${process.env.VUE_APP_API_URL}/data/widget?widget=${widgetCode}`)
          .then((response) => {
            commit('setWidget', { widget: response.data, mergeFields: query })
            amplitude.getInstance().logEvent('envelope_loaded_widget')
            return response
          })
      } else if (router.currentRoute.name === 'Preview') {
        return axios.get(`${process.env.VUE_APP_API_URL}/data/preview`, {
          params: {
            envelope_fingerprint: router.currentRoute.params.envelopeFingerprint,
            signature_fingerprint: router.currentRoute.params.signatureFingerprint
          }
        }).then((response) => {
          commit('setEnvelopeRegular', { data: response.data })

          return response
        })
      } else if ((router.currentRoute.name === 'BulkSendPreview')) {
        const queryString = new URLSearchParams(query).toString()
        return axios({
          method: 'get',
          baseURL: process.env.VUE_APP_API_URL,
          url: `/preview/bulk-send/${router.currentRoute.params.bulkSendImportedContactFingerprint}?${queryString}`
        }).then((response) => {
          commit('setEnvelopeRegular', { data: response.data })

          return response
        })
      }

      return axios.get(`${process.env.VUE_APP_API_URL}/data/envelope`, {
        params: {
          'passphrase': password
        }
      }).then((response) => {
        if (response.data.error) {
          throw response
        }
        commit('setEnvelopeRegular', { data: response.data })
        amplitude.getInstance().setUserId(response.data.client.client_id)
        amplitude.getInstance().logEvent('envelope_loaded_regular')
        return response
      })
    },
    setCurrentFieldVal: ({ commit, getters }, { val, id, property }) => {
      const field = getters.getField(id)
      const isWidget = getters.isWidget
      commit('setCurrentFieldValue', { field, val, property, isWidget })
    },
    setUploader: ({ commit }, { uploader, fieldId }) => {
      commit('setUploader', { uploader, fieldId })
    },
    nextDocument: ({ commit, getters }) => {
      if (getters.nextIncompleteDocumentIndex > -1) {
        commit('setDocument', getters.nextIncompleteDocumentIndex)
      }
    },
    setDocument: ({ commit, dispatch }, documentIndex) => {
      commit('setDocument', documentIndex)
      dispatch('setActiveField', null)
    },
    setActiveField: ({ commit, getters }, activeFieldId) => {
      commit('setActiveField', activeFieldId)
      commit('setFieldTouched', activeFieldId)

      const activatedField = getters.getField(activeFieldId)
      if (activatedField && activatedField.group) {
        getters
          .fieldsForDocument()
          .filter(field => field.group === activatedField.group)
          .forEach(field => commit('setFieldTouched', field.id))
      }

      for (let index = 0; index < getters.activeFieldIndex; index++) {
        const field = getters.fieldsForDocument()[index]
        commit('setFieldTouched', field.id)
      }
    },
    setLatestSignature: ({ commit, getters }, { property, value }) => {
      commit('setLatestSignature', { [property]: value })
      if (value) {
        commit('setSignatureToUse', property)
      } else {
        commit('setSignatureToUse', null)
      }
    },
    confirmSignature: ({ state, dispatch, getters }, id) => {
      const latest = getters.latestSignature

      if (state.signatureToUse && latest[state.signatureToUse]) {
        dispatch('setCurrentFieldVal', { id, val: null, property: 'drawn_json' })
        dispatch('setCurrentFieldVal', { id, val: null, property: 'content' })
        dispatch('setCurrentFieldVal', { id, val: null, property: 'upload' })

        dispatch('setCurrentFieldVal', { id, val: latest[state.signatureToUse], property: state.signatureToUse })
      }
    },
    setSignatureToUse: ({ commit }, tabProperty) => {
      commit('setSignatureToUse', tabProperty)
    },
    initSignatureToUse: ({ commit, getters }) => {
      commit('setSignatureToUse', getters.getSignatureToUse)
    }
  },
  getters: {
    title: state => state.envelope.title,
    isWidget: state => !!state.widget.fields,
    widgetRedirectUrl: state => state.widget.redirect_url,
    requestedEnvelope: state => state.requestedEnvelope,
    config: state => property => state.config[property],
    document: state => index => state.envelope.documents[index],
    allFields: (state, getters) => {
      let fields = []

      const extractFields = (docFields, docIndex = 0) => {
        const extractedFields = []
        const documentFields = Object.values(docFields)
        const pages = Object.keys(docFields)
        documentFields.forEach((pageFields, page) => {
          pageFields.forEach((field, index) => { // TODO Set doc number
            field.docIndex = docIndex
            field.page = pages[page]
            field.index = index
            extractedFields.push(field)
          })
        })

        return extractedFields
      }

      if (getters.isWidget) {
        fields = extractFields(state.widget.fields)
      } else if (state.envelope && state.envelope.documents.length > 0) {
        state.envelope.documents.forEach((doc, docIndex) => {
          fields = fields.concat(extractFields(doc.fields, docIndex))
        })
      }

      // when merge fields come back, 2 always comeback one with value,
      // other without. This function sanitizes this bug
      fields = Object.values(fields.reduce((acc, cur) =>
        Object.assign(acc, { [cur.id]: cur }), {}
      ))

      // sort fields by page and index
      return fields.sort((a, b) =>
        parseInt(a.page, 0) * 10000 + parseInt(a.index, 0) < parseInt(b.page, 0) * 10000 + parseInt(b.index, 0) ? -1 : 1)
    },
    fields: (_, getters) => getters.allFields.filter(field => field.party_id === getters.currentPartyId),
    fieldsCount: (_, getters) => getters.fields.length,
    doneFields: (_, getters) => getters.fields.filter(field => getters.fieldDone(field)),
    doneFieldsCount: (_, getters) => getters.doneFields.length,
    requiredFields: (_, getters) => {
      const fields = getters.fields
      const requiredFields = []

      // strip out fields not marked as required
      const requiredFilter = fields.filter(getters.fieldRequired)

      // strip out prefilled fields
      const requiredAndPrefilledFilter = requiredFilter.filter(field => !(field.is_merge_field && field.prefilled))

      // count multiple fields with the same group as one
      requiredAndPrefilledFilter.forEach(field => {
        const fieldGroupAlreadyAdded = !!requiredFields.find(f => f.group === field.group)
        if (field.group === '' || (field.group && !fieldGroupAlreadyAdded)) {
          requiredFields.push(field)
        }
      })
      return requiredFields
    },
    requiredFieldsCount: (_, getters) => getters.requiredFields.length,
    doneRequiredFields: (_, getters) => {
      return getters.requiredFields.filter(field => getters.fieldDone(field))
    },
    doneRequiredFieldsCount: (_, getters) => getters.doneRequiredFields.length,
    outstandingFieldsNo: (_, getters) => getters.requiredFieldsCount - getters.doneRequiredFieldsCount,
    /**
     * @return {Number}
     */
    envelopeCompletionPercent: (_, getters) => {
      if (getters.requiredFieldsCount === 0) {
        return 100
      } else if (getters.doneRequiredFieldsCount === 0) {
        return 0
      }

      return Math.round((getters.doneRequiredFieldsCount / getters.requiredFieldsCount) * 100)
    },
    isEnvelopeComplete: (_, getters) => {
      return getters.doneRequiredFieldsCount === getters.requiredFieldsCount
    },
    currentDocument: (state) => {
      if (state.envelope.documents && state.envelope.documents.length > 0) {
        return state.envelope.documents[state.currentDocumentIndex]
      }

      const widget = store.state.widget
      widget.pages = parseInt(widget.pages, 10)
      return widget
    },

    /**
     * Get the current document fingerprint
     * @return [String,Boolean] Returns a string or boolean
     */
    currentDocumentFingerprint: (state, getters) => {
      if (state.widget && state.widget.fingerprint) return state.widget.fingerprint
      const doc = getters.document(state.currentDocumentIndex)
      if (doc) return doc.fingerprint

      return false
    },
    fieldsForDocument: (state, getters) => (documentFingerprint) => {
      const documentIndex = getters.isWidget
        ? 0
        : documentFingerprint
          ? state.envelope.documents.findIndex(document => document.fingerprint === documentFingerprint)
          : state.currentDocumentIndex

      return getters.fields.filter(field => field.docIndex === documentIndex && !field.prefilled)
    },
    doneFieldsForDocument: (_, getters) => (documentFingerprint) => {
      const fieldsForDocument = getters.fieldsForDocument(documentFingerprint)
      return fieldsForDocument.filter(field => getters.fieldDone(field))
    },
    /**
     * Returns the current party id if available, else undefined
     * @return [String,undefined]
     */
    currentPartyId: (state) => {
      let currentPartyId
      state.parties.forEach((party) => {
        if (party.isSigner) {
          currentPartyId = party.party_id
        }
      })
      return currentPartyId
    },
    /**
     * Get the current party from the store
     * @return {Array<Object>}
     */
    currentParty: (state, getters) => {
      const currentPartyId = getters.currentPartyId
      return state.parties.filter(party => currentPartyId === party.party_id)[0]
    },
    getField: (_, getters) => (id) => getters.allFields.find(
      field => parseInt(field.id, 10) === parseInt(id, 10)
    ),
    getFieldForDocument: (_, getters) => (id) => getters.fieldsForDocument().find(
      field => parseInt(field.id, 10) === parseInt(id, 10)
    ),
    getFieldProperty: (_, getters) => (id, property) => {
      const field = getters.getField(id)

      if (field) {
        return field[property]
      }
    },
    activeFieldId: (state) => {
      return state.activeFieldId
    },
    activeGroup: (_, getters) => {
      if (getters.activeFieldIndex < 0) {
        return null
      }

      return getters.fieldsForDocument()[getters.activeFieldIndex].group
    },
    activeFieldIndex: (state, getters) => {
      return getters.fieldsForDocument().findIndex(field => field.id === state.activeFieldId)
    },
    onLastField: (_, getters) => {
      return getters.activeFieldIndex >= getters.fieldsForDocument().length - 1
    },
    nextField: (_, getters) => {
      const fields = getters.fieldsForDocument()
      const doneFields = getters.doneFieldsForDocument()
      const currentIndex = getters.activeFieldIndex
      const currentField = fields[currentIndex]

      if (!fields.length) {
        return
      }

      // When no active field or on last field
      // Find next field that isn't completed
      if (currentIndex < 0 || currentIndex >= fields.length - 1) {
        return fields.find(field => !doneFields.find(doneField => field.id === doneField.id))
      }

      // Loop previous fields and find first field that isn't completed
      for (let index = 0; index < currentIndex; index++) {
        const field = fields[index]

        if (!getters.fieldDone(field)) {
          return field
        }
      }

      // Find next field that isn't in current group
      for (let index = currentIndex + 1; index < fields.length; index++) {
        const field = fields[index]

        if (!field.group || field.group !== currentField.group) {
          return field
        }
      }
    },
    allowNextField: (state, getters) => {
      const currentField = getters.getFieldForDocument(state.activeFieldId)
      return getters.nextField && (!getters.fieldRequired(currentField) || getters.fieldDone(currentField))
    },
    // TODO for some reason the backend sends back 'true' or 'false' as strings to indicate if a field is required
    fieldRequired: (_, getters) => (field) => {
      // No field
      if (!field) {
        return false
      }

      // Field is required
      if (field.required === 'true') {
        return true
      }

      // Field is not required and not in a group
      if (!field.group) {
        return false
      }

      // If any field in the group is required they all are
      if (
        getters
          .fieldsForDocument()
          .some(docField => docField.group === field.group && docField.required === 'true')
      ) {
        return true
      }

      return false
    },
    fieldDone: (state, getters) => (field) => {
      if (!getters.fieldRequired(field) && state.touchedFieldIds.includes(field.id)) {
        return true
      }

      if (field.type === 'checkbox' && field.group) {
        return getters.fields.filter(f => f.group === field.group && f.type === 'checkbox').some(field => field.content === '1')
      }

      if (field.type === 'checkbox' && field.content !== '1') {
        return false
      }

      return field.content || (field.drawn_json && field.drawn_json.length) || (field.upload && field.upload.length)
    },
    isDocumentDone: (_, getters) => (documentFingerprint) => {
      const fingerprint = documentFingerprint || getters.currentDocumentFingerprint
      const fieldsForCurrentDoc = getters.fieldsForDocument(fingerprint)
      const doneFieldsForDocument = getters.doneFieldsForDocument(fingerprint)

      return doneFieldsForDocument.length >= fieldsForCurrentDoc.length
    },
    nextIncompleteDocumentIndex: (state, getters) => state.envelope.documents.findIndex((doc) => !getters.isDocumentDone(doc.fingerprint)),
    nextDocumentIndex: (state) => state.currentDocumentIndex + 1 <= state.envelope.documents.length - 1,
    signatures: state => state.signatures,
    envelope: state => state.envelope,
    latestSignature: state => state.latestSignature,
    hasSignature: (state, getters) => {
      const field = getters.getField(getters.activeFieldId)

      if (!state.signatureToUse) {
        return !!(field.drawn_json || field.content || field.upload)
      }

      if (state.latestSignature[state.signatureToUse] || field[state.signatureToUse]) {
        return true
      }
    },
    getSignatureToUse: (state, getters) => {
      // Get from field, 'confirmed' signature
      const field = getters.getField(getters.activeFieldId)
      if (field && field.content) {
        return 'content'
      }
      if (field && field.drawn_json) {
        return 'drawn_json'
      }
      if (field && field.upload) {
        return 'upload'
      }

      // Get from state, last tab with content
      if (state.signatureToUse) {
        return state.signatureToUse
      }

      // Get from latest
      if (state.latestSignature.content) {
        return 'content'
      }
      if (state.latestSignature.drawn_json) {
        return 'drawn_json'
      }
      if (state.latestSignature.upload) {
        return 'upload'
      }

      return translateSignatureTypes(state.config.company_signature_default)
    },
    canShowQuestions: (_, getters) => getters.config('company_questions_signing') === '1' && !getters.isWidget
  }
})

/**
 * Translate Signature Types
 * change between config and local versions
 *
 * @param {String} type
 */
function translateSignatureTypes (type) {
  if (type === 'drawn') {
    return 'drawn_json'
  }
  if (type === 'typed') {
    return 'content'
  }

  return type
}

export default store
